import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check, Clock, Heart, Mail, MapPin, Smile, Sparkles, Users } from "lucide-react";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import Footer from "@/sections/Footer";
import { Eyebrow, MaskedLine, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASTER_TIMELINE = [
  { mins: "5 min", text: "Hello song and meet Bailey" },
  { mins: "15 min", text: "Interactive reading with props and call-and-response" },
  { mins: "10 min", text: "Act it out: children move, pretend and join the story" },
  { mins: "5 min", text: "Affirmation circle: “I am brave, I am kind, I can try”" },
  { mins: "10 min", text: "Questions for the author and a goodbye wave" },
];

const TASTER_INCLUDES = [
  "Printable colouring sheet for every child",
  "Parent letter with signed-book pre-order option",
];

const MORNING_AGES = [
  { age: "2–3 yrs", text: "20-minute sensory storytime: puppets, textures, songs and repeated phrases" },
  { age: "3–4 yrs", text: "30-minute story and movement session with a feelings talk" },
  { age: "4–5 yrs", text: "40-minute story, Q&A with the author and a drawing activity ready for Reception" },
];

const MORNING_INCLUDES = [
  "Meet the author and book signing",
  "Positive affirmations and creative activity",
  "Printable activity for every child",
  "Follow-up ideas for key workers",
  "Parent pre-order for signed books",
];

const THEMES = [
  {
    name: "Healthy smiles",
    book: "Bailey Visits the Dentist",
    img: "/images/book-bailey-dentist.jpg",
    text: "Makes the first dental visit feel familiar. Children practise brushing on a giant model, name what happens at a check-up and talk about feeling nervous.",
    link: "EYFS link: Personal, Social & Emotional Development, Managing Self (oral health)",
  },
  {
    name: "Out & about",
    book: "Bailey in London",
    img: "/images/book-bailey-in-london.jpeg",
    text: "Children spot red buses, taxis and London's famous landmarks — getting to see London through the eyes of a young explorer.",
    link: "EYFS link: Understanding the World, People & Communities",
  },
  {
    name: "I am brave",
    book: "Bailey's Affirmations",
    img: "/images/book-bailey-affirmations.jpg",
    text: "A gentle session of self-belief and positive self-image, using the 'I am' affirmations children can say together.",
    link: "EYFS link: PSED, Self-Regulation; Communication & Language",
  },
  {
    name: "Starting school",
    book: "Bailey Starts School",
    img: "/images/book-bailey-starts-school-new.jpeg",
    text: "First days feel friendly. Children talk about new routines, meeting teachers and making friends, and practise a brave wave goodbye.",
    link: "EYFS link: PSED, Managing Self; Communication & Language",
  },
  {
    name: "I can do it",
    book: "Bailey, What Can You Do?",
    img: "/images/book-bailey-what-can-you-do.jpeg",
    text: "Children discover all the things they can do — playing football, getting dressed and tidying up — and celebrate every 'I did it!' together.",
    link: "EYFS link: Physical Development; PSED, Building Relationships",
  },
];

const CURRICULUM = [
  { area: "Communication & Language", do: "Listen, join in with repeated phrases, answer and ask questions", framework: "EYFS prime area" },
  { area: "Personal, Social & Emotional", do: "Name feelings, practise affirmations, learn about looking after teeth", framework: "EYFS prime area · PSHE" },
  { area: "Literacy", do: "Handle books, retell the story, meet a real author", framework: "EYFS specific area · KS1 English" },
  { area: "Understanding the World", do: "Talk about places in London and people who help us", framework: "EYFS specific area · KS1 Geography" },
  { area: "Expressive Arts & Design", do: "Act out, draw and invent new Bailey adventures", framework: "EYFS specific area · KS1 Art" },
];

const DAMALI_BRINGS = [
  "DBS certificate, available to view on request",
  "Public liability insurance",
  "Props and all activity materials",
  "Printable activity sheets, sent ahead for copying",
  "Parent pre-order letter for signed books",
  "Invoice after booking",
];

const WE_ASK = [
  "A member of staff with each group at all times",
  "A carpet space or hall; a screen is helpful for KS1 sessions",
  "Group sizes and any SEND or language needs in advance",
  "Your photo consent policy, so photos are only taken where agreed",
  "A table near the exit for book signing at pick-up",
];

const BOOKING_STEPS = [
  { n: "1", title: "Send your dates", text: "Email two or three preferred dates, the age groups and roughly how many children." },
  { n: "2", title: "Agree the plan", text: "Damali confirms the sessions and timetable and sends the invoice." },
  { n: "3", title: "Share with parents", text: "Two weeks before, you send the pre-order letter so families can order signed books." },
];

export default function AuthorVisitsPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, type: "author-visit" });
      toast.success("Thank you — Damali will reply personally to plan your visit.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
  };

  const input = "w-full rounded-xl border border-sand bg-white/80 px-4 py-3 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition";

  return (
    <div className="grain">
      <Navbar />
      <main className="pt-28 sm:pt-32 pb-24 overflow-x-clip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* HERO */}
          <div className="text-center max-w-3xl mx-auto mb-6">
            <Eyebrow className="mb-5">Author visits for nurseries & primary schools · London</Eyebrow>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-walnut">
              <MaskedLine i={0}>Bailey's</MaskedLine>
              <MaskedLine i={1} className="text-terracotta">Author Visits</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 text-base sm:text-lg text-walnutsoft leading-relaxed"
            >
              Interactive storytime with children's author Damali Allison. Children meet
              the author, step into Bailey's adventures, and leave with a little more
              confidence, a lot more to say, and a love of books.
            </motion.p>
            <motion.div
              data-testid="visits-hero-chips"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="mt-7 flex flex-wrap justify-center gap-3"
            >
              {["Ages 2–7 · EYFS & KS1", "5 published books", "Nursery sessions from £150"].map((c) => (
                <span key={c} className="inline-flex items-center gap-2 rounded-full bg-white/85 border border-sand px-4 py-2 text-xs sm:text-sm font-semibold text-walnut shadow-sm">
                  <Sparkles size={14} className="text-amberbrand" /> {c}
                </span>
              ))}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.8 }}
              className="mt-8 flex flex-wrap justify-center gap-4"
            >
              <button
                data-testid="visits-see-sessions"
                onClick={() => scrollTo("visits")}
                className="group rounded-full bg-terracotta text-cream pl-7 pr-6 py-4 font-display font-bold text-sm sm:text-base inline-flex items-center gap-2 hover:bg-terracottadark transition-colors"
              >
                See the sessions <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="visits-book-cta"
                onClick={() => scrollTo("booking")}
                className="rounded-full border-2 border-walnut text-walnut px-7 py-[0.85rem] font-display font-bold text-sm sm:text-base hover:bg-walnut hover:text-cream transition-colors"
              >
                Book a visit
              </button>
            </motion.div>
          </div>

          {/* STORYTIME CIRCLE */}
          <Reveal className="mb-24">
            <div data-testid="storytime-circle" className="relative bg-white/70 border border-sand rounded-[2rem] px-6 sm:px-10 pt-10 pb-8 text-center overflow-hidden">
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-walnut">Storytime circle</h2>
              <p className="mt-2 text-sm sm:text-base text-walnutsoft">Listen, have fun and learn with Bailey</p>
              <motion.img
                src="/images/circle-time.png"
                alt="Damali reading a Bailey book to children sitting in a circle on the storytime mat"
                data-testid="circle-time-image"
                className="mx-auto mt-8 w-full max-w-xl drop-shadow-[0_18px_28px_rgba(49,67,92,0.25)]"
                initial={{ y: 50, opacity: 0, scale: 0.92 }}
                whileInView={{ y: 0, opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ type: "spring", stiffness: 90, damping: 12 }}
              />
              <div className="mt-8 mx-auto max-w-xl flex items-center gap-4 bg-creamdark/60 border border-sand rounded-2xl p-4 text-left">
                <img
                  src="/images/book-bailey-starts-school-new.jpeg"
                  alt="Bailey Starts School book cover"
                  className="w-14 h-16 object-cover rounded-lg shadow shrink-0"
                />
                <div className="flex-1">
                  <p className="font-display font-bold text-sm sm:text-base text-walnut">Story time</p>
                  <p className="text-xs sm:text-sm text-walnutsoft">Picture taken from the book Bailey Starts School.</p>
                </div>
                <Link
                  to="/books/bailey-starts-school"
                  data-testid="circle-time-buy-book"
                  className="shrink-0 rounded-full bg-terracotta text-cream font-display font-bold text-xs sm:text-sm px-5 py-2.5 hover:bg-terracottadark transition-colors"
                >
                  Get the book
                </Link>
              </div>
            </div>
          </Reveal>

          {/* MEET THE AUTHOR */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-24">
            <Reveal>
              <div className="relative">
                <div className="spotlight rounded-[2rem] border border-sand p-6 sm:p-8 overflow-hidden">
                  <img
                    src="/images/author-reading-classroom.jpg"
                    alt="Damali Allison reading a Bailey book to children at an author visit"
                    className="w-full aspect-[4/5] object-cover rounded-2xl shadow-[0_30px_60px_-24px_rgba(49,67,92,0.4)]"
                  />
                </div>
                <div className="absolute -bottom-6 -left-3 sm:-left-6 bg-walnut text-cream rounded-2xl px-6 py-4 shadow-xl rotate-[-2deg]">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta mb-1">Meet the author</p>
                  <p className="font-display font-bold text-lg leading-tight">Damali Allison</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <Eyebrow className="mb-4">Meet the author</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut mb-5">
                Damali Allison and the Bailey Adventure Book Series
              </h2>
              <p className="text-sm sm:text-base text-walnutsoft leading-relaxed mb-4">
                Damali is a London-based children's author with five published books in the
                Bailey Adventure Book Series. Each story follows Bailey through an everyday
                experience young children know well, from a trip to the dentist to a day
                out in the city.
              </p>
              <p className="text-sm sm:text-base text-walnutsoft leading-relaxed mb-7">
                Visits are built around those moments. Children listen, join in, act out the
                story and talk about what Bailey feels, so the session supports communication,
                confidence and early reading at the same time.
              </p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-4 bg-white/70 border border-sand rounded-2xl px-5 py-3.5">
                  <img src="/images/book-bailey-dentist.jpg" alt="Bailey Visits the Dentist book" className="w-10 h-12 object-cover rounded-md shadow" />
                  <div>
                    <p className="font-display font-bold text-sm sm:text-base text-walnut">Bailey Visits the Dentist</p>
                    <p className="text-xs sm:text-sm text-walnutsoft">Healthy smiles, brave feelings, first check-ups</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/70 border border-sand rounded-2xl px-5 py-3.5">
                  <img src="/images/book-bailey-in-london.jpeg" alt="Bailey in London book" className="w-10 h-12 object-cover rounded-md shadow" />
                  <div>
                    <p className="font-display font-bold text-sm sm:text-base text-walnut">Bailey in London</p>
                    <p className="text-xs sm:text-sm text-walnutsoft">Landmarks, journeys and the city on our doorstep</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/70 border border-sand rounded-2xl px-5 py-3.5">
                  <img src="/images/book-bailey-affirmations.jpg" alt="Bailey's Affirmations book" className="w-10 h-12 object-cover rounded-md shadow" />
                  <div>
                    <p className="font-display font-bold text-sm sm:text-base text-walnut">Bailey's Affirmations</p>
                    <p className="text-xs sm:text-sm text-walnutsoft">Little "I am" words that grow big confidence</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/70 border border-sand rounded-2xl px-5 py-3.5">
                  <img src="/images/book-bailey-starts-school-new.jpeg" alt="Bailey Starts School book" className="w-10 h-12 object-cover rounded-md shadow" />
                  <div>
                    <p className="font-display font-bold text-sm sm:text-base text-walnut">Bailey Starts School</p>
                    <p className="text-xs sm:text-sm text-walnutsoft">A friendly introduction to starting school — making friends and first lessons, all on the very first day</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-white/70 border border-sand rounded-2xl px-5 py-3.5">
                  <img src="/images/book-bailey-what-can-you-do.jpeg" alt="Bailey, What Can You Do? book" className="w-10 h-12 object-cover rounded-md shadow" />
                  <div>
                    <p className="font-display font-bold text-sm sm:text-base text-walnut">Bailey, What Can You Do?</p>
                    <p className="text-xs sm:text-sm text-walnutsoft">Playing football, getting dressed and tidying up — cheering every "I did it!"</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* SESSIONS */}
          <div id="visits" className="scroll-mt-28 mb-24">
            <Reveal className="text-center mb-12">
              <Eyebrow className="mb-4">Choose your visit</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut">
                Sessions for nurseries and schools
              </h2>
              <p className="mt-3 text-sm sm:text-base text-walnutsoft">Nursery · ages 2–5 &nbsp;·&nbsp; Primary school · Reception–Year 2</p>
            </Reveal>

            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Taster */}
              <Reveal>
                <div data-testid="session-taster" className="h-full bg-white/75 border border-sand rounded-3xl p-7 sm:p-9 hover:-translate-y-1.5 hover:shadow-[0_24px_44px_-20px_rgba(49,67,92,0.3)] transition-all duration-300">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <h3 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-walnut">Storytime Taster</h3>
                    <span className="text-right shrink-0">
                      <span className="block font-display font-extrabold text-2xl sm:text-3xl text-walnut">£150</span>
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-creamdark/70 border border-sand px-3 py-1.5 text-xs font-semibold text-walnut"><Clock size={13} className="text-terracotta" /> 45 minutes</span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-creamdark/70 border border-sand px-3 py-1.5 text-xs font-semibold text-walnut"><Users size={13} className="text-terracotta" /> 1 group, up to 20 children</span>
                  </div>
                  <p className="text-sm sm:text-base text-walnutsoft leading-relaxed mb-6">
                    A single, high-energy storytime that fits between snack and lunch. A good
                    first visit for settings trying an author session for the first time.
                  </p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amberbrand mb-4">How the session runs</p>
                  <ol className="flex flex-col gap-3 mb-6">
                    {TASTER_TIMELINE.map((t) => (
                      <li key={t.mins + t.text} className="flex items-start gap-3 text-sm text-walnutsoft">
                        <span className="shrink-0 w-14 text-center font-mono font-bold text-xs text-terracotta bg-terracotta/10 rounded-full px-2 py-1 mt-0.5">{t.mins}</span>
                        {t.text}
                      </li>
                    ))}
                  </ol>
                  <ul className="flex flex-col gap-2.5 border-t border-sand pt-5">
                    {TASTER_INCLUDES.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-walnut">
                        <Check size={16} className="text-sage shrink-0 mt-0.5" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              {/* Nursery Morning */}
              <Reveal delay={0.12}>
                <div data-testid="session-morning" className="relative h-full bg-walnut text-cream rounded-3xl p-7 sm:p-9 overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_24px_44px_-20px_rgba(49,67,92,0.45)] transition-all duration-300">
                  <span className="absolute top-6 right-6 rounded-full bg-amberbrand text-cream text-[11px] font-bold uppercase tracking-widest px-3.5 py-1.5 shadow">
                    Most popular
                  </span>
                  <div className="absolute w-72 h-72 rounded-full bg-terracotta/20 blur-[100px] -top-16 -right-16 pointer-events-none" />
                  <div className="relative z-10">
                    <div className="flex items-start justify-between gap-4 mb-2 pr-24">
                      <h3 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight">Nursery Morning</h3>
                      <span className="font-display font-extrabold text-2xl sm:text-3xl shrink-0">£250</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-6">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs font-semibold"><Clock size={13} className="text-terracotta" /> Half day</span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs font-semibold"><Users size={13} className="text-terracotta" /> 2–3 room sessions</span>
                    </div>
                    <p className="text-sm sm:text-base text-cream/75 leading-relaxed mb-6">
                      Damali moves room to room so each age group gets a session pitched for
                      them, with the whole setting sharing the same Bailey story.
                    </p>
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta mb-4">Pitched by age</p>
                    <div className="flex flex-col gap-3 mb-6">
                      {MORNING_AGES.map((a) => (
                        <div key={a.age} className="flex items-start gap-3 text-sm text-cream/85">
                          <span className="shrink-0 w-16 text-center font-mono font-bold text-xs text-walnut bg-creamdark rounded-full px-2 py-1 mt-0.5">{a.age}</span>
                          {a.text}
                        </div>
                      ))}
                    </div>
                    <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2.5 border-t border-white/15 pt-5">
                      {MORNING_INCLUDES.map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-sm text-cream/85">
                          <Check size={16} className="text-sage shrink-0 mt-0.5" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* THEMES */}
          <div className="mb-24">
            <Reveal className="text-center mb-12">
              <Eyebrow className="mb-4">Pick a theme</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut">
                Sessions built around a Bailey story
              </h2>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {THEMES.map((t, i) => (
                <Reveal key={t.name} delay={i * 0.08}>
                  <div data-testid={`theme-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group h-full bg-white/75 border border-sand rounded-3xl overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_24px_44px_-20px_rgba(49,67,92,0.3)] transition-all duration-300">
                    <div className="h-40 sm:h-48 overflow-hidden">
                      <img src={t.img} alt={`${t.book} book`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-6">
                      <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amberbrand mb-1.5">{t.name}</p>
                      <h3 className="font-display font-bold text-xl tracking-tight text-walnut mb-2">{t.book}</h3>
                      <p className="text-sm text-walnutsoft leading-relaxed mb-4">{t.text}</p>
                      <p className="text-xs font-semibold text-sage leading-relaxed border-t border-sand pt-3">{t.link}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* CURRICULUM */}
          <Reveal className="mb-24">
            <div data-testid="curriculum-table" className="bg-white/75 border border-sand rounded-[2rem] p-7 sm:p-10 overflow-hidden">
              <Eyebrow className="mb-4">For your planning</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-walnut mb-8">
                Where the visit fits your curriculum
              </h2>
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full text-left text-sm min-w-[560px]">
                  <thead>
                    <tr className="border-b-2 border-sand">
                      <th className="py-3 pr-4 font-mono text-[11px] uppercase tracking-[0.2em] text-amberbrand">Area</th>
                      <th className="py-3 pr-4 font-mono text-[11px] uppercase tracking-[0.2em] text-amberbrand">What children do</th>
                      <th className="py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-amberbrand">Framework</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CURRICULUM.map((r) => (
                      <tr key={r.area} className="border-b border-sand/70 last:border-0 hover:bg-white/60 transition-colors">
                        <td className="py-4 pr-4 font-display font-bold text-walnut align-top">{r.area}</td>
                        <td className="py-4 pr-4 text-walnutsoft align-top">{r.do}</td>
                        <td className="py-4 text-walnutsoft align-top"><span className="inline-block rounded-full bg-sage/15 border border-sage/30 text-sage text-xs font-semibold px-3 py-1">{r.framework}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>

          {/* PRACTICAL DETAILS */}
          <div className="mb-24">
            <Reveal className="text-center mb-12">
              <Eyebrow className="mb-4">Practical details</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut">Before the day</h2>
            </Reveal>
            <div className="grid md:grid-cols-2 gap-6">
              <Reveal>
                <div data-testid="damali-brings" className="h-full bg-white/75 border border-sand rounded-3xl p-7 sm:p-8">
                  <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-walnut mb-5">What Damali brings</h3>
                  <ul className="flex flex-col gap-3">
                    {DAMALI_BRINGS.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm sm:text-base text-walnutsoft">
                        <Check size={17} className="text-sage shrink-0 mt-0.5" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div data-testid="we-ask" className="h-full bg-white/75 border border-sand rounded-3xl p-7 sm:p-8">
                  <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-walnut mb-5">What we ask of you</h3>
                  <ul className="flex flex-col gap-3">
                    {WE_ASK.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm sm:text-base text-walnutsoft">
                        <Check size={17} className="text-terracotta shrink-0 mt-0.5" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <p className="mt-6 text-center text-sm sm:text-base text-walnutsoft inline-flex items-center gap-2 w-full justify-center">
                <MapPin size={16} className="text-terracotta" /> Visits are available across London. Travel is included within zones to confirm.
              </p>
            </Reveal>
          </div>

          {/* BOOKING */}
          <div id="booking" className="scroll-mt-28">
            <Reveal className="text-center mb-12">
              <Eyebrow className="mb-4">Booking</Eyebrow>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut">
                Three steps to a Bailey visit
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-3 gap-6 mb-14">
              {BOOKING_STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.08}>
                  <div data-testid={`booking-step-${s.n}`} className="h-full bg-white/75 border border-sand rounded-3xl p-7 text-center hover:-translate-y-1.5 hover:shadow-[0_24px_44px_-20px_rgba(49,67,92,0.3)] transition-all duration-300">
                    <span className="inline-flex w-12 h-12 rounded-full bg-terracotta text-cream font-display font-extrabold text-xl items-center justify-center mb-4 shadow">{s.n}</span>
                    <h3 className="font-display font-bold text-lg sm:text-xl tracking-tight text-walnut mb-2">{s.title}</h3>
                    <p className="text-sm text-walnutsoft leading-relaxed">{s.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal>
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <Eyebrow className="mb-4">Get in touch</Eyebrow>
                  <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-walnut mb-4">
                    Damali Allison · Author / Learn with Bailey
                  </h2>
                  <a
                    href="mailto:hello@learnwithbailey.org"
                    data-testid="visits-email-link"
                    className="inline-flex items-center gap-2.5 font-display font-bold text-lg sm:text-xl text-terracotta hover:text-terracottadark transition-colors mb-4"
                  >
                    <Mail size={20} /> hello@learnwithbailey.org
                  </a>
                  <p className="text-sm sm:text-base text-walnutsoft leading-relaxed flex items-start gap-2">
                    <Heart size={16} className="text-terracotta shrink-0 mt-1" />
                    More about the books at <a href="https://www.learnwithbailey.org" target="_blank" rel="noopener noreferrer" className="font-semibold text-walnut underline decoration-terracotta/50 hover:decoration-terracotta">learnwithbailey.org</a>
                  </p>
                  <p className="mt-5 text-sm text-walnutsoft leading-relaxed max-w-md">
                    Or send your dates using the form — include your setting's name, two or
                    three preferred dates, the age groups and roughly how many children.
                  </p>
                </div>
                <form
                  data-testid="visit-booking-form"
                  onSubmit={submit}
                  className="bg-white/75 border border-sand rounded-3xl p-7 sm:p-8 shadow-[0_30px_60px_-28px_rgba(49,67,92,0.35)]"
                >
                  <div className="flex flex-col gap-3">
                    <input
                      data-testid="visit-name-input"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name and setting (nursery / school)"
                      className={input}
                    />
                    <input
                      data-testid="visit-email-input"
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="Your email address"
                      className={input}
                    />
                    <textarea
                      data-testid="visit-message-input"
                      required
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Preferred dates, age groups and how many children…"
                      className={`${input} resize-none`}
                    />
                    <button
                      data-testid="visit-submit-button"
                      type="submit"
                      disabled={sending}
                      className="group mt-1 rounded-full bg-terracotta text-cream py-4 font-display font-bold text-sm sm:text-base flex items-center justify-center gap-2 hover:bg-terracottadark transition-colors disabled:opacity-60"
                    >
                      {sending ? "Sending…" : "Send visit enquiry"}
                      <Smile size={17} className="group-hover:rotate-12 transition-transform" />
                    </button>
                  </div>
                </form>
              </div>
            </Reveal>
          </div>

        </div>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
